import React, { useEffect, useState, useRef } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import { usageService, paymentService } from '../services/api';
import { BarChart3Icon, DollarSignIcon, XIcon, RefreshCwIcon, ReceiptIcon, ExternalLinkIcon, CalendarIcon } from 'lucide-react';
import { format, startOfDay, endOfDay, getUnixTime } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { loadStripe } from '@stripe/stripe-js';

// Utility function to format storage values dynamically
const formatStorageValue = (gbValue) => {
  if (!gbValue || gbValue === 0) return '0 GB';
  
  // Convert GB to KB (1 GB = 1,048,576 KB)
  const kbValue = gbValue * 1024 * 1024;
  
  // Determine the best unit based on value
  if (gbValue >= 1) {
    // Display in GB if >= 1 GB
    return `${gbValue.toFixed(2)} GB`;
  } else if (gbValue >= 0.001) {
    // Display in MB if >= 1 MB (0.001 GB)
    const mbValue = gbValue * 1024;
    return `${mbValue.toFixed(2)} MB`;
  } else {
    // Display in KB for smaller values
    return `${kbValue.toFixed(0)} KB`;
  }
};

// Shimmer loading components
const ShimmerLine = ({ width = 'w-full', height = 'h-4' }) => (
  <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded ${width} ${height}`}></div>
);

const ShimmerCircle = ({ size = 'h-10 w-10' }) => (
  <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded-full ${size}`}></div>
);

const ShimmerRect = ({ width = 'w-full', height = 'h-20' }) => (
  <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded-md ${width} ${height}`}></div>
);

function Usage() {
  const [isLoading, setIsLoading] = useState(true);
  const [balanceData, setBalanceData] = useState(null);
  const [breakdownData, setBreakdownData] = useState(null);
  const [tokenUsageHistory, setTokenUsageHistory] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [displayedPayments, setDisplayedPayments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePayments, setHasMorePayments] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const paymentsPerPage = 5;
  
  // Token usage state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  
  // State to track which graph is currently being hovered
  const [activeGraph, setActiveGraph] = useState(null);
  const [hourlyUsageData, setHourlyUsageData] = useState(null);
  const [isLoadingUsage, setIsLoadingUsage] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState(10);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [usePromoCode, setUsePromoCode] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const { notify } = useNotification();

  // Single useEffect to fetch all data
  // Initialize displayed payments when payment history changes
  useEffect(() => {
    if (paymentHistory.length > 0) {
      // Reset to first page when new payment history is loaded
      setCurrentPage(1);
      setDisplayedPayments(paymentHistory.slice(0, paymentsPerPage));
      setHasMorePayments(paymentHistory.length > paymentsPerPage);
    } else {
      setDisplayedPayments([]);
      setHasMorePayments(false);
    }
  }, [paymentHistory, paymentsPerPage]);

  // Create refs outside of useEffect to fix React Hook rules
  const notificationHasRun = useRef(false);
  
  // Handle payment success or cancellation and credit notifications
  useEffect(() => {
    const handlePaymentResult = async () => {
      // Skip if this effect has already run
      if (notificationHasRun.current) return;
      notificationHasRun.current = true;
      
      const urlParams = new URLSearchParams(window.location.search);
      const paymentSuccess = urlParams.get('payment_success');
      const paymentCancelled = urlParams.get('payment_cancelled');
      
      // Check for credit notification flag from sessionStorage
      const showCreditNotification = sessionStorage.getItem('showCreditNotification');
      if (showCreditNotification === 'true') {
        // Show notification after page has loaded
        setTimeout(() => {
          notify.error('You have insufficient credits. Please top up your account.');
          // Remove the flag to prevent showing again on refresh
          sessionStorage.removeItem('showCreditNotification');
        }, 500);
      }
      
      if (paymentSuccess === 'true') {
        notify.success('Payment completed successfully');
        // Refresh balance data and payment history with a slight delay to ensure backend has updated
        setTimeout(async () => {
          // Fetch both in parallel
          const [newBalance, paymentData] = await Promise.all([
            usageService.getBalance(),
            usageService.getPaymentHistory()
          ]);
          
          console.log('Updated balance from URL handler:', newBalance);
          setBalanceData(newBalance);
          setPaymentHistory(paymentData.payments || []);
        }, 1000);
        // Clean up URL parameters
        window.history.replaceState({}, document.title, '/usage');
      } else if (paymentCancelled === 'true') {
        notify.info('Payment was cancelled');
        // Clean up URL parameters
        window.history.replaceState({}, document.title, '/usage');
      }
    };
    
    handlePaymentResult();
  }, []);

  // Load more payments when page changes
  useEffect(() => {
    // Skip the first page since it's handled by the first useEffect
    if (currentPage > 1 && paymentHistory.length > 0) {
      const endIndex = currentPage * paymentsPerPage;
      const newPayments = paymentHistory.slice((currentPage - 1) * paymentsPerPage, endIndex);
      
      // Append new payments to existing ones
      setDisplayedPayments(prev => [...prev, ...newPayments]);
      setHasMorePayments(paymentHistory.length > endIndex);
    }
  }, [currentPage, paymentHistory, paymentsPerPage]);

  // Load more payments
  const loadMorePayments = () => {
    setCurrentPage(prevPage => prevPage + 1);
    setExpanded(true);
  };
  
  // Show less payments (truncate back to first page)
  const showLessPayments = () => {
    setCurrentPage(1);
    setDisplayedPayments(paymentHistory.slice(0, paymentsPerPage));
    setExpanded(false);
    setHasMorePayments(paymentHistory.length > paymentsPerPage);
  };

  // Create ref for data fetching outside useEffect
  const hasDataFetchRun = useRef(false);
  
  useEffect(() => {
    const fetchAllData = async () => {
      // Skip if this effect has already run
      if (hasDataFetchRun.current) return;
      hasDataFetchRun.current = true;
      
      try {
        setIsLoading(true);
        
        // Fetch all data in parallel
        const [balanceData, payments] = await Promise.all([
          usageService.getBalance(),
          usageService.getPaymentHistory()
        ]);
        
        // Set state with fetched data
        setBalanceData(balanceData);
        if (payments && payments.payments) {
          setPaymentHistory(payments.payments);
        }
        
      } catch (error) {
        console.error('Error fetching usage data:', error);
        // notify.error('Failed to load usage data');
        
        // Set default values if data is missing
        if (!balanceData) {
          setBalanceData({
            available_balance: 0.0,
            reported_balance: 0.0,
            unbilled_usage: 0.0,
            currency: 'usd',
            updated_at: new Date().toISOString()
          });
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAllData();
  }, []);

  useEffect(() => {
    // Track if component is mounted to prevent state updates after unmount
    let isMounted = true;
    const fetchHourlyUsageData = async () => {
      try {
        if (isMounted) setIsLoadingUsage(true);
        
        // Calculate start and end timestamps in UTC to align with Stripe's hourly boundaries
        // Extract date parts and create explicit UTC dates to avoid timezone issues
        const year = selectedDate.getFullYear();
        const month = selectedDate.getMonth();
        const date = selectedDate.getDate();
        
        // Create UTC timestamps: start of day (00:00:00 UTC) and start of next day (00:00:00 UTC)
        const startDateUTC = new Date(Date.UTC(year, month, date, 0, 0, 0, 0));
        const endDateUTC = new Date(Date.UTC(year, month, date + 1, 0, 0, 0, 0));
        
        const startTimestamp = getUnixTime(startDateUTC);
        const endTimestamp = getUnixTime(endDateUTC);
        
        // Fetch hourly usage data
        const data = await usageService.getHourlyUsageData(startTimestamp, endTimestamp);
        if (isMounted) setHourlyUsageData(data);
      } catch (error) {
        console.error('Error fetching hourly usage data:', error);
        // Removed notify call to prevent potential infinite loop
      } finally {
        if (isMounted) setIsLoadingUsage(false);
      }
    };
    
    fetchHourlyUsageData();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [selectedDate]);

  // Handle top-up button click
  const handleTopUp = () => {
    setShowTopUpModal(true);
  };
  
  // Close top-up modal
  const closeTopUpModal = () => {
    setShowTopUpModal(false);
  };
  
  // Initialize Stripe with publishable key
  const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY 
    ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
    : null;

  // Process payment
  const processPayment = async () => {
    try {
      setProcessingPayment(true);
      
      // Call payment API to get client_secret or process promo code
      const response = usePromoCode
        ? await paymentService.processPayment(null, promoCode)
        : await paymentService.processPayment(topUpAmount);
      
      console.log('Payment response:', response);
      
      // Handle promo code response
      if (response && response.promo_applied) {
        // Promo code was successfully applied
        setShowTopUpModal(false);
        setProcessingPayment(false);
        // Clear promo code state
        setPromoCode('');
        setUsePromoCode(false);
        notify.success(`Promo code applied! ${formatCurrency(response.promo_amount / 100)} has been added to your account.`);
        
        // Refresh balance data with a slight delay to ensure backend has updated
        setTimeout(async () => {
          // Fetch both balance and payment history in parallel
          const [newBalance, paymentData] = await Promise.all([
            usageService.getBalance(),
            usageService.getPaymentHistory()
          ]);
          
          console.log('Updated balance after promo code:', newBalance);
          setBalanceData(newBalance);
          setPaymentHistory(paymentData.payments || []);
        }, 1000);
        
        return;
      }
      
      // Handle regular payment with Stripe
      if (response && response.client_secret) {
        // Load Stripe.js
        const stripe = await stripePromise;
        
        if (!stripe) {
          throw new Error('Failed to load Stripe');
        }

        // We need to use Stripe Elements instead of direct URL redirect
        // Create a payment element and mount it to the DOM
        const elements = stripe.elements({
          clientSecret: response.client_secret,
          appearance: {
            theme: 'stripe',
          },
        });

        // Close the modal since we'll be showing Stripe's UI
        setShowTopUpModal(false);
        
        // Create a container for Stripe Elements if it doesn't exist
        let container = document.getElementById('stripe-payment-container');
        if (!container) {
          container = document.createElement('div');
          container.id = 'stripe-payment-container';
          container.style.position = 'fixed';
          container.style.zIndex = '9999';
          container.style.left = '0';
          container.style.top = '0';
          container.style.width = '100%';
          container.style.height = '100%';
          container.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
          container.style.display = 'flex';
          container.style.justifyContent = 'center';
          container.style.alignItems = 'center';
          document.body.appendChild(container);
          
          const paymentElement = elements.create('payment');
          
          // Create a wrapper with white background and padding
          const wrapper = document.createElement('div');
          wrapper.style.backgroundColor = 'white';
          wrapper.style.borderRadius = '8px';
          wrapper.style.padding = '20px';
          wrapper.style.width = '500px';
          wrapper.style.maxWidth = '100%';
          container.appendChild(wrapper);
          
          // Add a title
          const title = document.createElement('h2');
          title.textContent = 'Complete Your Payment';
          title.style.marginBottom = '20px';
          title.style.fontSize = '20px';
          title.style.fontWeight = 'bold';
          wrapper.appendChild(title);
          
          // Create payment element container
          const paymentContainer = document.createElement('div');
          wrapper.appendChild(paymentContainer);
          paymentElement.mount(paymentContainer);
          
          // Add a pay button
          const payButton = document.createElement('button');
          payButton.textContent = 'Pay Now';
          payButton.style.marginTop = '20px';
          payButton.style.backgroundColor = '#4F46E5';
          payButton.style.color = 'white';
          payButton.style.padding = '10px 16px';
          payButton.style.borderRadius = '4px';
          payButton.style.border = 'none';
          payButton.style.cursor = 'pointer';
          payButton.style.width = '100%';
          wrapper.appendChild(payButton);
          
          // Add a cancel button
          const cancelButton = document.createElement('button');
          cancelButton.textContent = 'Cancel';
          cancelButton.style.marginTop = '10px';
          cancelButton.style.backgroundColor = 'transparent';
          cancelButton.style.color = '#6B7280';
          cancelButton.style.padding = '10px 16px';
          cancelButton.style.borderRadius = '4px';
          cancelButton.style.border = '1px solid #E5E7EB';
          cancelButton.style.cursor = 'pointer';
          cancelButton.style.width = '100%';
          wrapper.appendChild(cancelButton);
          
          // Handle payment submission
          payButton.addEventListener('click', async () => {
            // Disable the button during processing
            payButton.disabled = true;
            payButton.innerHTML = '<span class="spinner"></span> Processing...';
            payButton.style.opacity = '0.7';
            
            // Add a spinner style
            const style = document.createElement('style');
            style.textContent = `
              .spinner {
                display: inline-block;
                width: 16px;
                height: 16px;
                border: 2px solid rgba(255,255,255,0.3);
                border-radius: 50%;
                border-top-color: white;
                animation: spin 1s ease-in-out infinite;
                margin-right: 8px;
              }
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `;
            document.head.appendChild(style);
            
            const { error, paymentIntent } = await stripe.confirmPayment({
              elements,
              redirect: 'if_required',
            });
            
            if (error) {
              // Payment failed but keep the modal open
              payButton.disabled = false;
              payButton.innerHTML = 'Try Again';
              payButton.style.opacity = '1';
              
              // Create an error message element if it doesn't exist
              let errorElement = document.getElementById('payment-error-message');
              if (!errorElement) {
                errorElement = document.createElement('div');
                errorElement.id = 'payment-error-message';
                errorElement.style.color = '#e53e3e';
                errorElement.style.backgroundColor = '#fed7d7';
                errorElement.style.padding = '12px';
                errorElement.style.borderRadius = '4px';
                errorElement.style.marginBottom = '16px';
                errorElement.style.fontSize = '14px';
                
                // Insert before the payment element container
                paymentContainer.parentNode.insertBefore(errorElement, paymentContainer);
              }
              
              // Update the error message
              errorElement.textContent = error.message || 'Payment failed. Please try again.';
              
              // Scroll to the error message
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            } else if (paymentIntent && paymentIntent.status === 'succeeded') {
              // Payment succeeded - show success message and clean up
              document.body.removeChild(container);
              setProcessingPayment(false);
              notify.success('Payment completed successfully');
              
              // Refresh balance data and payment history with a slight delay to ensure backend has updated
              setTimeout(async () => {
                // Fetch both in parallel
                const [newBalance, paymentData] = await Promise.all([
                  usageService.getBalance(),
                  usageService.getPaymentHistory()
                ]);
                
                console.log('Updated balance after payment:', newBalance);
                setBalanceData(newBalance);
                setPaymentHistory(paymentData.payments || []);
              }, 1000);
            }
          });
          
          // Handle cancel
          cancelButton.addEventListener('click', () => {
            document.body.removeChild(container);
            setProcessingPayment(false);
            notify.info('Payment cancelled');
          });
          
          // Allow users to update card details and try again
          elements.on('change', (event) => {
            // Clear error message when user updates their details
            const errorElement = document.getElementById('payment-error-message');
            if (errorElement && event.complete) {
              errorElement.style.display = 'none';
            }
          });
        }
      } else {
        throw new Error('Invalid payment response');
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      notify.error('Failed to process payment. Please try again.');
      setProcessingPayment(false);
      setShowTopUpModal(false);
      setPromoCode('');
      setUsePromoCode(false);
    }
  };
  
  // Format currency
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };
  
  // Format date
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MM/dd/yyyy, h:mm a');
    } catch (error) {
      return 'Unknown date';
    }
  };

  // State for active tab
  const [activeTab, setActiveTab] = useState('usage');

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Usage and Payments</h1>

      {/* Account Balance Section */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-6 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Account Balance</h2>
          <button
            onClick={handleTopUp}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-md flex items-center space-x-2"
            disabled={isLoading}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="16" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
            <span>Top Up</span>
          </button>
        </div>
        
        <div className="px-6 pb-6">
          <div className="flex flex-col items-center text-center py-4">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Available Balance</p>
            
            {isLoading ? (
              <>
                <div className="mt-2 mb-3">
                  <ShimmerLine width="w-32" height="h-10" />
                </div>
                <ShimmerLine width="w-48" height="h-3" />
              </>
            ) : (
              <>
                <p className="text-4xl font-bold text-gray-900 dark:text-white mt-2">
                  {balanceData ? formatCurrency(balanceData.available_balance) : '$0.00'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  Last updated: {balanceData ? formatDate(balanceData.updated_at) : 'Unknown'}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tab Switcher Section */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex items-center justify-center p-2">
          <button
            onClick={() => setActiveTab('usage')}
            className={`flex-1 py-3 px-6 text-center font-medium text-sm transition-all duration-200 ${
              activeTab === 'usage' 
                ? 'text-blue-600 bg-blue-50 rounded-full mx-2 dark:text-blue-400 dark:bg-blue-900/20' 
                : 'text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800 rounded-full mx-2'
            }`}
          >
            Usage
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`flex-1 py-3 px-6 text-center font-medium text-sm transition-all duration-200 ${
              activeTab === 'payments' 
                ? 'text-blue-600 bg-blue-50 rounded-full mx-2 dark:text-blue-400 dark:bg-blue-900/20' 
                : 'text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800 rounded-full mx-2'
            }`}
          >
            Payment History
          </button>
        </div>
      </div>

      {/* Content Sections - Conditionally rendered based on active tab */}
      {activeTab === 'payments' && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Payment History (Top Up)</h2>
          
          {isLoading ? (
            <div className="space-y-4">
              {/* Shimmer for table header */}
              <div className="grid grid-cols-5 gap-4">
                <ShimmerLine height="h-3" width="w-16" />
                <ShimmerLine height="h-3" width="w-16" />
                <ShimmerLine height="h-3" width="w-24" />
                <ShimmerLine height="h-3" width="w-16" />
                <ShimmerLine height="h-3" width="w-16" />
              </div>
              
              {/* Shimmer for table rows */}
              {[...Array(3)].map((_, index) => (
                <div key={index} className="grid grid-cols-5 gap-4 py-4 border-t border-gray-200 dark:border-gray-700">
                  <ShimmerLine height="h-4" width="w-20" />
                  <ShimmerLine height="h-4" width="w-16" />
                  <ShimmerLine height="h-4" width="w-32" />
                  <ShimmerLine height="h-4" width="w-14" />
                  <ShimmerLine height="h-4" width="w-16" />
                </div>
              ))}
            </div>
          ) : paymentHistory.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead>
                    <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      <th className="px-6 py-3">Date</th>
                      <th className="px-6 py-3">Amount</th>
                      <th className="px-6 py-3">Payment Method</th>
                      <th className="px-6 py-3">Status</th>
                      <th className="px-6 py-3">Receipt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {displayedPayments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {format(new Date(payment.created * 1000), 'MMM d, yyyy, h:mm a')}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                          {formatCurrency(payment.amount / 100, payment.currency)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center">
                            <span className="capitalize">{payment.card_brand}</span>
                            <span className="ml-1">•••• {payment.card_last4}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            payment.status === 'succeeded' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400' 
                              : payment.status === 'pending' 
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800/20 dark:text-yellow-400' 
                                : 'bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-400'
                          }`}>
                            {payment.status === 'succeeded' ? 'Paid' : payment.status === 'pending' ? 'Pending' : 'Failed'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {payment.receipt_url && (
                            <a 
                              href={payment.receipt_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1.5 border border-blue-100 bg-blue-50 hover:bg-blue-100 rounded text-xs font-medium text-blue-600 dark:border-blue-800/30 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 dark:text-blue-400 transition-colors"
                              aria-label="View Receipt"
                            >
                              <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                              </svg>
                              View
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Controls */}
              <div className="mt-4 text-center">
                {hasMorePayments && (
                  <button
                    onClick={loadMorePayments}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800 mr-3"
                  >
                    Load More
                  </button>
                )}
                {expanded && (
                  <button
                    onClick={showLessPayments}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800"
                  >
                    Show Less
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <ReceiptIcon size={36} className="mx-auto text-gray-400 dark:text-gray-600 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No payment history available</p>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Token Usage Section - Only shown when activeTab is 'usage' */}
      {activeTab === 'usage' && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Usage Summary for {format(selectedDate, 'MMMM d, yyyy')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Hourly breakdown of resource usage</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Data is updated hourly • Times shown in UTC</p>
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCalendar(!showCalendar)}
                className="flex items-center px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                disabled={isLoadingUsage}
              >
                <CalendarIcon size={16} className="mr-2" />
                {format(selectedDate, 'MM/dd/yyyy')}
              </button>
              
              {showCalendar && (
                <div className="absolute right-0 z-10 mt-2 bg-white rounded-md shadow-xl dark:bg-gray-800 border border-gray-200 dark:border-gray-700" style={{ minWidth: '320px' }}>
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-medium text-gray-900 dark:text-white">{format(selectedDate, 'MMMM yyyy')}</h3>
                      <div className="flex space-x-3">
                      <button
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => {
                          const newDate = new Date(selectedDate);
                          newDate.setMonth(newDate.getMonth() - 1);
                          setSelectedDate(newDate);
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => {
                          const newDate = new Date(selectedDate);
                          newDate.setMonth(newDate.getMonth() + 1);
                          setSelectedDate(newDate);
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-7 gap-3 mb-3">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                      <div key={index} className="text-center text-sm font-medium text-gray-600 dark:text-gray-400">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  <div className="grid grid-cols-7 gap-3">
                    {Array.from({ length: 42 }).map((_, index) => {
                      const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
                      date.setDate(index - date.getDay() + 1);
                      
                      const isCurrentMonth = date.getMonth() === selectedDate.getMonth();
                      const isToday = new Date().toDateString() === date.toDateString();
                      const isSelected = selectedDate.toDateString() === date.toDateString();
                      
                      return (
                        <button
                          key={index}
                          onClick={() => {
                            setSelectedDate(date);
                            setShowCalendar(false);
                          }}
                          className={`
                            h-10 w-10 rounded-full flex items-center justify-center text-sm font-medium
                            ${isCurrentMonth ? '' : 'opacity-30'}
                            ${isSelected ? 'bg-blue-500 text-white' : ''}
                            ${isToday && !isSelected ? 'border-2 border-blue-500' : ''}
                            ${!isSelected && isCurrentMonth ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : ''}
                          `}
                        >
                          {date.getDate()}
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="mt-6 flex justify-between">
                    <button
                      className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                      onClick={() => {
                        setSelectedDate(new Date());
                        setShowCalendar(false);
                      }}
                    >
                      Today
                    </button>
                    <button
                      className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                      onClick={() => setShowCalendar(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="p-6">
          {isLoadingUsage ? (
            <div className="h-80 flex flex-col justify-center items-center space-y-4">
              <ShimmerRect width="w-full" height="h-64" />
              <div className="flex justify-center space-x-8 w-full">
                <ShimmerLine width="w-24" height="h-4" />
                <ShimmerLine width="w-24" height="h-4" />
              </div>
            </div>
          ) : hourlyUsageData && Object.keys(hourlyUsageData.meters).some(key => hourlyUsageData.meters[key].total > 0) ? (
            <div className="space-y-8">
              {/* Input Tokens Section */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Input Tokens</h4>
                <div className="h-64">
                  {hourlyUsageData.meters.chat_input_tokens.total > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={hourlyUsageData.meters.chat_input_tokens.data_points.map(point => ({
                          timestamp: point.timestamp,
                          time: format(new Date(point.timestamp * 1000), 'h a'),
                          value: point.value
                        })).sort((a, b) => a.timestamp - b.timestamp)}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        onMouseEnter={() => setActiveGraph('input_tokens')}
                        onMouseLeave={() => setActiveGraph(null)}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                        <XAxis
                          dataKey="time"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          axisLine={{ stroke: '#E5E7EB' }}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            padding: '12px 16px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                          formatter={(value) => [`${value.toLocaleString()} tokens`, 'Input Tokens']}
                          labelFormatter={(time) => `${time}`}
                          wrapperStyle={{ zIndex: 10 }}
                          cursor={false}
                          active={activeGraph === 'input_tokens'}
                        />
                        <Bar
                          dataKey="value"
                          name="Input Tokens"
                          fill="#8884d8"
                          radius={[4, 4, 0, 0]}
                          barSize={30}
                          animationDuration={1500}
                          isAnimationActive={true}
                          activeBar={activeGraph === 'input_tokens' ? { fill: '#7367f0', stroke: '#7367f0' } : null}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                      <p className="text-lg font-medium mb-2">No data</p>
                      <p className="text-sm">No input tokens usage for this period</p>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <div className="bg-white dark:bg-gray-800 p-3 rounded-md shadow-sm">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Total Input Tokens</p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{hourlyUsageData.meters.chat_input_tokens.total.toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Cost</p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{formatCurrency(hourlyUsageData.meters.chat_input_tokens.cost || 0)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Output Tokens Section */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg mt-8">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Output Tokens</h4>
                <div className="h-64">
                  {hourlyUsageData.meters.chat_output_tokens.total > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={hourlyUsageData.meters.chat_output_tokens.data_points.map(point => ({
                          timestamp: point.timestamp,
                          time: format(new Date(point.timestamp * 1000), 'h a'),
                          value: point.value
                        })).sort((a, b) => a.timestamp - b.timestamp)}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        onMouseEnter={() => setActiveGraph('output_tokens')}
                        onMouseLeave={() => setActiveGraph(null)}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                        <XAxis
                          dataKey="time"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          axisLine={{ stroke: '#E5E7EB' }}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            padding: '12px 16px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                          formatter={(value) => [`${value.toLocaleString()} tokens`, 'Output Tokens']}
                          labelFormatter={(time) => `${time}`}
                          wrapperStyle={{ zIndex: 10 }}
                          cursor={false}
                          active={activeGraph === 'output_tokens'}
                        />
                        <Bar
                          dataKey="value"
                          name="Output Tokens"
                          fill="#82ca9d"
                          radius={[4, 4, 0, 0]}
                          barSize={30}
                          animationDuration={1500}
                          isAnimationActive={true}
                          activeBar={activeGraph === 'output_tokens' ? { fill: '#4cd137', stroke: '#4cd137' } : null}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                      <p className="text-lg font-medium mb-2">No data</p>
                      <p className="text-sm">No output tokens usage for this period</p>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <div className="bg-white dark:bg-gray-800 p-3 rounded-md shadow-sm">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Total Output Tokens</p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{hourlyUsageData.meters.chat_output_tokens.total.toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Cost</p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{formatCurrency(hourlyUsageData.meters.chat_output_tokens.cost || 0)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Model Inference Section */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg mt-8">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Model Inference</h4>
                <div className="h-64">
                  {(() => {
                    // Check if third-party invocations exist and should override the view
                    const hasThirdPartyInvocations = hourlyUsageData.meters.model_inference_third_party_invocations?.total > 0;
                    const hasInvocationSeconds = hourlyUsageData.meters.model_inference_invocation_seconds?.total > 0;
                    const hasSageMaker = hourlyUsageData.meters.model_inference_units_sagemaker?.total > 0;
                    
                    // If both third-party and regular invocation seconds exist, prioritize third-party
                    const useThirdParty = hasThirdPartyInvocations && hasInvocationSeconds;
                    const showThirdParty = hasThirdPartyInvocations && !hasInvocationSeconds;
                    
                    // Determine which data to show
                    const hasData = hasThirdPartyInvocations || hasInvocationSeconds || hasSageMaker;
                    
                    if (!hasData) {
                      return (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400">
                          <p className="text-lg font-medium mb-2">No data</p>
                          <p className="text-sm">No model inference usage for this period</p>
                        </div>
                      );
                    }
                    
                    // Determine unit based on what we're showing
                    const unit = (useThirdParty || showThirdParty) ? 'invocations' : 'seconds';
                    
                    // Build the data array
                    let dataPoints = [];
                    
                    if (useThirdParty || showThirdParty) {
                      // Show third-party invocations
                      dataPoints = (hourlyUsageData.meters.model_inference_third_party_invocations?.data_points || []).map(point => ({
                        timestamp: point.timestamp,
                        time: format(new Date(point.timestamp * 1000), 'h a'),
                        value: point.value,
                        source: 'Third-party'
                      }));
                    } else {
                      // Show regular inference data
                      dataPoints = [
                        ...(hourlyUsageData.meters.model_inference_units_sagemaker?.data_points || []).map(point => ({
                          timestamp: point.timestamp,
                          time: format(new Date(point.timestamp * 1000), 'h a'),
                          value: point.value,
                          source: 'SageMaker'
                        })),
                        ...(hourlyUsageData.meters.model_inference_invocation_seconds?.data_points || []).map(point => ({
                          timestamp: point.timestamp,
                          time: format(new Date(point.timestamp * 1000), 'h a'),
                          value: point.value,
                          source: 'Serverless'
                        }))
                      ]
                      // Combine values for the same timestamp
                      .reduce((acc, item) => {
                        const existingItem = acc.find(i => i.timestamp === item.timestamp);
                        if (existingItem) {
                          existingItem.value += item.value;
                          existingItem.sources = [...(existingItem.sources || [existingItem.source]), item.source];
                          delete existingItem.source;
                        } else {
                          acc.push(item);
                        }
                        return acc;
                      }, []);
                    }
                    
                    return (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={dataPoints.sort((a, b) => a.timestamp - b.timestamp)}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                          onMouseEnter={() => setActiveGraph('model_inference')}
                          onMouseLeave={() => setActiveGraph(null)}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                          <XAxis
                            dataKey="time"
                            tick={{ fill: '#6B7280', fontSize: 12 }}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickLine={false}
                          />
                          <YAxis
                            tick={{ fill: '#6B7280', fontSize: 12 }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                const displayValue = unit === 'invocations' 
                                  ? `${Math.floor(data.value).toLocaleString()} ${unit}` 
                                  : `${data.value.toFixed(3)} ${unit}`;
                                return (
                                  <div className="bg-white dark:bg-gray-800 p-2 shadow rounded">
                                    <p className="text-sm text-gray-900 dark:text-white">
                                      {`${data.time}: ${displayValue}`}
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                            wrapperStyle={{ zIndex: 10 }}
                            cursor={false}
                            active={activeGraph === 'model_inference'}
                          />
                          <Bar
                            dataKey="value"
                            name={unit === 'invocations' ? 'Invocations' : 'Processing Time (Seconds)'}
                            fill="#82ca9d"
                            radius={[4, 4, 0, 0]}
                            barSize={30}
                            animationDuration={1500}
                            isAnimationActive={true}
                            activeBar={activeGraph === 'model_inference' ? { fill: '#4cd137', stroke: '#4cd137' } : null}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </div>
                {/* Bottom cost display for consistency with other sections */}
                {(() => {
                  const hasThirdPartyInvocations = hourlyUsageData.meters.model_inference_third_party_invocations?.total > 0;
                  const hasInvocationSeconds = hourlyUsageData.meters.model_inference_invocation_seconds?.total > 0;
                  const hasSageMaker = hourlyUsageData.meters.model_inference_units_sagemaker?.total > 0;
                  
                  const useThirdParty = hasThirdPartyInvocations && hasInvocationSeconds;
                  const showThirdParty = hasThirdPartyInvocations && !hasInvocationSeconds;
                  
                  const hasData = hasThirdPartyInvocations || hasInvocationSeconds || hasSageMaker;
                  
                  if (!hasData) return null;
                  
                  let totalValue, totalCost, metricLabel, unit;
                  
                  if (useThirdParty || showThirdParty) {
                    // Show third-party metrics
                    totalValue = hourlyUsageData.meters.model_inference_third_party_invocations?.total || 0;
                    totalCost = hourlyUsageData.meters.model_inference_third_party_invocations?.cost || 0;
                    metricLabel = 'Total Invocations';
                    unit = 'invocations';
                  } else {
                    // Show regular metrics
                    totalValue = (hourlyUsageData.meters.model_inference_units_sagemaker?.total || 0) + 
                               (hourlyUsageData.meters.model_inference_invocation_seconds?.total || 0);
                    totalCost = (hourlyUsageData.meters.model_inference_units_sagemaker?.cost || 0) +
                               (hourlyUsageData.meters.model_inference_invocation_seconds?.cost || 0);
                    metricLabel = 'Total Processing Time';
                    unit = 'seconds';
                  }
                  
                  return (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{metricLabel}</p>
                        <p className="text-lg font-medium text-gray-900 dark:text-white">
                          {unit === 'invocations' 
                            ? `${Math.floor(totalValue).toLocaleString()} ${unit}` 
                            : `${totalValue.toFixed(2)} ${unit}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Total Cost</p>
                        <p className="text-lg font-medium text-gray-900 dark:text-white">
                          {formatCurrency(totalCost)}
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
              
              {/* Model Build Input Tokens Section */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg mt-8">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Model Build Input Tokens</h4>
                <div className="h-64">
                  {hourlyUsageData.meters.model_build_input_tokens.total > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={hourlyUsageData.meters.model_build_input_tokens.data_points.map(point => ({
                          timestamp: point.timestamp,
                          time: format(new Date(point.timestamp * 1000), 'h a'),
                          value: point.value
                        })).sort((a, b) => a.timestamp - b.timestamp)}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        onMouseEnter={() => setActiveGraph('model_build_input')}
                        onMouseLeave={() => setActiveGraph(null)}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                        <XAxis
                          dataKey="time"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          axisLine={{ stroke: '#E5E7EB' }}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            padding: '12px 16px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                          formatter={(value) => [`${value.toLocaleString()} tokens`, 'Model Build Input']}
                          labelFormatter={(time) => `${time}`}
                          wrapperStyle={{ zIndex: 10 }}
                          cursor={false}
                          active={activeGraph === 'model_build_input'}
                        />
                        <Bar
                          dataKey="value"
                          name="Model Build Input Tokens"
                          fill="#9c88ff"
                          radius={[4, 4, 0, 0]}
                          barSize={30}
                          animationDuration={1500}
                          isAnimationActive={true}
                          activeBar={activeGraph === 'model_build_input' ? { fill: '#8c7ae6', stroke: '#8c7ae6' } : null}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                      <p className="text-lg font-medium mb-2">No data</p>
                      <p className="text-sm">No model build input tokens for this period</p>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <div className="bg-white dark:bg-gray-800 p-3 rounded-md shadow-sm">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Total Model Build Input Tokens</p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{hourlyUsageData.meters.model_build_input_tokens.total.toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Cost</p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{formatCurrency(hourlyUsageData.meters.model_build_input_tokens.cost || 0)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Model Build Output Tokens Section */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg mt-8">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Model Build Output Tokens</h4>
                <div className="h-64">
                  {hourlyUsageData.meters.model_build_output_tokens.total > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={hourlyUsageData.meters.model_build_output_tokens.data_points.map(point => ({
                          timestamp: point.timestamp,
                          time: format(new Date(point.timestamp * 1000), 'h a'),
                          value: point.value
                        })).sort((a, b) => a.timestamp - b.timestamp)}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        onMouseEnter={() => setActiveGraph('model_build_output')}
                        onMouseLeave={() => setActiveGraph(null)}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                        <XAxis
                          dataKey="time"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          axisLine={{ stroke: '#E5E7EB' }}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            padding: '12px 16px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                          formatter={(value) => [`${value.toLocaleString()} tokens`, 'Model Build Output']}
                          labelFormatter={(time) => `${time}`}
                          wrapperStyle={{ zIndex: 10 }}
                          cursor={false}
                          active={activeGraph === 'model_build_output'}
                        />
                        <Bar
                          dataKey="value"
                          name="Model Build Output Tokens"
                          fill="#badc58"
                          radius={[4, 4, 0, 0]}
                          barSize={30}
                          animationDuration={1500}
                          isAnimationActive={true}
                          activeBar={activeGraph === 'model_build_output' ? { fill: '#a3cb38', stroke: '#a3cb38' } : null}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                      <p className="text-lg font-medium mb-2">No data</p>
                      <p className="text-sm">No model build output tokens for this period</p>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <div className="bg-white dark:bg-gray-800 p-3 rounded-md shadow-sm">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Total Model Build Output Tokens</p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{hourlyUsageData.meters.model_build_output_tokens.total.toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Cost</p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{formatCurrency(hourlyUsageData.meters.model_build_output_tokens.cost || 0)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Model Storage Section */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg mt-8">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Model Storage</h4>
                <div className="h-64">
                  {hourlyUsageData.meters.model_storage_gb.total > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={hourlyUsageData.meters.model_storage_gb.data_points.map(point => ({
                          timestamp: point.timestamp,
                          time: format(new Date(point.timestamp * 1000), 'h a'),
                          value: point.value
                        })).sort((a, b) => a.timestamp - b.timestamp)}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        onMouseEnter={() => setActiveGraph('model_storage')}
                        onMouseLeave={() => setActiveGraph(null)}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                        <XAxis
                          dataKey="time"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          axisLine={{ stroke: '#E5E7EB' }}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(value) => value === 0 ? '0' : `${(value * 1000000).toFixed(2)}`}
                          domain={[0, 'dataMax * 1.1']}
                          scale='linear'
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            padding: '12px 16px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                          formatter={(value) => [formatStorageValue(value), 'Model Storage']}
                          labelFormatter={(time) => `${time}`}
                          wrapperStyle={{ zIndex: 10 }}
                          cursor={false}
                          active={activeGraph === 'model_storage'}
                        />
                        <Bar
                          dataKey="value"
                          name="Model Storage (GB)"
                          fill="#ff7979"
                          radius={[4, 4, 0, 0]}
                          barSize={30}
                          animationDuration={1500}
                          isAnimationActive={true}
                          activeBar={activeGraph === 'model_storage' ? { fill: '#ea8685', stroke: '#ea8685' } : null}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                      <p className="text-lg font-medium mb-2">No data</p>
                      <p className="text-sm">No model storage usage for this period</p>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <div className="bg-white dark:bg-gray-800 p-3 rounded-md shadow-sm">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Total Model Storage</p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{hourlyUsageData.meters.model_storage_gb.data_points.length > 0 ? 
                          formatStorageValue(Math.max(...hourlyUsageData.meters.model_storage_gb.data_points.map(point => point.value))) : '0 GB'
                        }</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Cost</p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{formatCurrency(hourlyUsageData.meters.model_storage_gb.cost || 0)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Dataset Storage Section */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg mt-8">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Dataset Storage</h4>
                <div className="h-64">
                  {hourlyUsageData.meters.dataset_storage_gb.total > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={hourlyUsageData.meters.dataset_storage_gb.data_points.map(point => ({
                          timestamp: point.timestamp,
                          time: format(new Date(point.timestamp * 1000), 'h a'),
                          value: point.value
                        })).sort((a, b) => a.timestamp - b.timestamp)}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        onMouseEnter={() => setActiveGraph('dataset_storage')}
                        onMouseLeave={() => setActiveGraph(null)}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                        <XAxis
                          dataKey="time"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          axisLine={{ stroke: '#E5E7EB' }}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(value) => value === 0 ? '0' : `${(value * 1000000).toFixed(2)}`}
                          domain={[0, 'dataMax * 1.1']}
                          scale='linear'
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            padding: '12px 16px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                          formatter={(value) => [formatStorageValue(value), 'Dataset Storage']}
                          labelFormatter={(time) => `${time}`}
                          wrapperStyle={{ zIndex: 10 }}
                          cursor={false}
                          active={activeGraph === 'dataset_storage'}
                        />
                        <Bar
                          dataKey="value"
                          name="Dataset Storage (GB)"
                          fill="#ffc658"
                          radius={[4, 4, 0, 0]}
                          barSize={30}
                          animationDuration={1500}
                          isAnimationActive={true}
                          activeBar={activeGraph === 'dataset_storage' ? { fill: '#ffab00', stroke: '#ffab00' } : null}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                      <p className="text-lg font-medium mb-2">No data</p>
                      <p className="text-sm">No dataset storage usage for this period</p>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <div className="bg-white dark:bg-gray-800 p-3 rounded-md shadow-sm">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Total Dataset Storage</p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{hourlyUsageData.meters.dataset_storage_gb.data_points.length > 0 ? 
                          formatStorageValue(Math.max(...hourlyUsageData.meters.dataset_storage_gb.data_points.map(point => point.value))) : '0 GB'
                        }</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Cost</p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{formatCurrency(hourlyUsageData.meters.dataset_storage_gb.cost || 0)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-80 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
              <BarChart3Icon size={48} className="mb-4 opacity-50" />
              <p className="text-lg">No usage data available for {format(selectedDate, 'MMMM d, yyyy')}</p>
              <p className="text-sm mt-2">Select a different date or try again later</p>
            </div>
          )}
        </div>
      </div>
      )}
      
      {/* Top Up Modal */}
      {showTopUpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top Up Your Account</h3>
              <button onClick={closeTopUpModal} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                <XIcon size={20} />
              </button>
            </div>
            
            <div className="p-6">
              {/* No tab switcher - just a title */}
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                {usePromoCode ? 'Enter Promo Code' : 'Enter Amount'}
              </h4>
              
              {!usePromoCode ? (
                <div className="mb-4">
                  <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 dark:text-gray-400">$</span>
                    </div>
                    <input
                      type="text"
                      id="amount"
                      value={topUpAmount}
                      onChange={(e) => {
                        // Allow direct editing of the field
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        if (value === '') {
                          setTopUpAmount('');
                        } else {
                          const numValue = Number(value);
                          if (!isNaN(numValue)) {
                            setTopUpAmount(value);
                          }
                        }
                      }}
                      onBlur={() => {
                        // Only enforce minimum on blur
                        if (topUpAmount === '' || Number(topUpAmount) < 10) {
                          setTopUpAmount(10);
                        }
                      }}
                      className="pl-7 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-slate-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-900 dark:text-white"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 dark:text-gray-400">USD</span>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Minimum amount: $10</p>
                  <button
                    onClick={() => setUsePromoCode(true)}
                    className="mt-2 text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300 text-left"
                  >
                    I have a promo code
                  </button>
                </div>
              ) : (
                <div className="mb-4">
                  <label htmlFor="promoCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Promo Code</label>
                  <input
                    type="text"
                    id="promoCode"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Enter your promo code"
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-slate-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-900 dark:text-white px-4 py-2"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Enter a valid promo code to receive account credit</p>
                  <button
                    onClick={() => setUsePromoCode(false)}
                    className="mt-2 text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300 text-left"
                  >
                    Back to amount
                  </button>
                </div>
              )}
              
              {/* Spacer */}
              <div className="mt-6"></div>
              
              <div className="mt-6 flex justify-between">
                <button
                  onClick={closeTopUpModal}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={processPayment}
                  disabled={processingPayment || (usePromoCode ? !promoCode.trim() : Number(topUpAmount) < 10)}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {processingPayment ? (
                    <>
                      <RefreshCwIcon size={16} className="animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>{usePromoCode ? 'Apply Promo Code' : 'Proceed to Payment'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Usage;